import { useEffect } from "react";

const kioskDefaultTheme = {
  "landing-background": "8 45 29",
  "landing-headline": "163 248 117",
  "landing-subheadline": "160 178 164",
  "landing-button-background": "163 248 117",
  "landing-button-text": "8 45 29",

  "actions-sheet-background": "8 45 29",
  "actions-sheet-primary-background": "163 248 117",
  "actions-sheet-primary-text": "8 45 29",
  "actions-sheet-secondary-background": "24 62 45",
  "actions-sheet-secondary-text": "255 255 255",

  "product-button-primary-background": "8 45 29",
  "product-button-primary-text": "163 248 117"
} as const;

type KioskTheme = Partial<Record<keyof typeof kioskDefaultTheme, string>>;

/**
 * Hook for applying a theme to the kiosk UI
 *
 * @param theme - Optional object containing CSS variable values to set
 *
 * This hook applies the given theme by setting CSS variables on the root element.
 * Each key in the theme object should match a CSS variable name (without the '--kiosk-' prefix).
 *
 * Example usage:
 * ```tsx
 * // Apply custom theme
 * useKioskTheme({ theme: { "text-color-primary": "0 0 0" } });
 * ```
 */
export const useKioskTheme = ({ theme }: { theme?: KioskTheme }) => {
  useEffect(() => {
    if (!theme) return;

    const root = document.documentElement;

    // Loop through theme object and set CSS variables
    Object.entries(theme).forEach(([name, value]) => {
      root.style.setProperty(`--kiosk-${name}`, value);
    });

    // Cleanup function to remove previously applied CSS variables
    return () => {
      Object.keys(theme).forEach((name) => {
        root.style.removeProperty(`--kiosk-${name}`);
      });
    };
  }, [theme]);
};
